import * as THREE from 'three';

/**
 * Apply Z-range clipping to mine map geometry using clipping planes.
 * This works for both single-mesh GLB models and multi-object DXF scenes.
 *
 * @param model  The root Object3D of the loaded mine map.
 * @param zRange If null, clipping is removed (level = 'all').
 *               Otherwise {min, max} defines the visible Z band.
 */
export function applyLevelFilter(
  model: THREE.Object3D | null,
  zRange: {min: number; max: number} | null,
): void {
  if (!model) return;

  if (!zRange) {
    // Remove clipping — show everything
    model.traverse((child: THREE.Object3D) => {
      child.visible = true;
      if (child instanceof THREE.Mesh) {
        clearMaterialClipping(child);
      }
    });
    return;
  }

  // Two clipping planes to constrain Z within [min, max]:
  // Plane 1: keeps z >= min (normal points +Z, constant = -min)
  // Plane 2: keeps z <= max (normal points -Z, constant = +max)
  const planes = [
    new THREE.Plane(new THREE.Vector3(0, 0, 1), -zRange.min),
    new THREE.Plane(new THREE.Vector3(0, 0, -1), zRange.max),
  ];

  model.traverse((child: THREE.Object3D) => {
    child.visible = true;
    if (child instanceof THREE.Mesh) {
      applyMaterialClipping(child, planes);
    }
  });
}

function applyMaterialClipping(mesh: THREE.Mesh, planes: THREE.Plane[]) {
  if (Array.isArray(mesh.material)) {
    mesh.material.forEach(m => {
      m.clippingPlanes = planes;
      m.clipShadows = true;
      m.needsUpdate = true;
    });
  } else if (mesh.material) {
    (mesh.material as THREE.Material).clippingPlanes = planes;
    (mesh.material as THREE.Material).clipShadows = true;
    (mesh.material as THREE.Material).needsUpdate = true;
  }
}

function clearMaterialClipping(mesh: THREE.Mesh) {
  if (Array.isArray(mesh.material)) {
    mesh.material.forEach(m => {
      m.clippingPlanes = null;
      m.clipShadows = false;
      m.needsUpdate = true;
    });
  } else if (mesh.material) {
    (mesh.material as THREE.Material).clippingPlanes = null;
    (mesh.material as THREE.Material).clipShadows = false;
    (mesh.material as THREE.Material).needsUpdate = true;
  }
}
